import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, Save } from "lucide-react";

export default function MasterDataTab() {
  return (
    <Tabs defaultValue="customer-types" className="w-full" data-testid="master-data-tab">
      <TabsList className="bg-muted/30">
        <TabsTrigger value="customer-types" className="rounded-sm" data-testid="tab-cust-types">Customer Types</TabsTrigger>
        <TabsTrigger value="vendor-types" className="rounded-sm" data-testid="tab-vend-types">Vendor Types</TabsTrigger>
        <TabsTrigger value="pr-reasons" className="rounded-sm" data-testid="tab-pr-reasons">PR Reasons &amp; Workflow</TabsTrigger>
      </TabsList>
      <TabsContent value="customer-types" className="mt-4"><TypeEditor kind="customer-types" accountField="revenue_account_code" accountLabel="Revenue Account" /></TabsContent>
      <TabsContent value="vendor-types" className="mt-4"><TypeEditor kind="vendor-types" accountField="expense_account_code" accountLabel="Expense Account" /></TabsContent>
      <TabsContent value="pr-reasons" className="mt-4"><PRReasonsEditor /></TabsContent>
    </Tabs>
  );
}

function TypeEditor({ kind, accountField, accountLabel }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newRow, setNewRow] = useState({ code: "", name: "", [accountField]: "", description: "" });

  const load = async () => {
    const r = await api.get(`/master/${kind}`);
    setRows(r.data);
  };
  useEffect(() => { load(); }, [kind]);

  const saveRow = async (row) => {
    setBusy(true);
    try {
      await api.post(`/master/${kind}`, row, { params: row.id ? { id: row.id } : {} });
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const addNew = async () => {
    if (!newRow.name) return toast.error("Name is required");
    await saveRow(newRow);
    setNewRow({ code: "", name: "", [accountField]: "", description: "" });
  };
  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/master/${kind}/${id}`);
    toast.success("Deleted");
    await load();
  };
  const update = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch, _dirty: true } : r)));
  };

  return (
    <div className="card-sharp p-5 space-y-4" data-testid={`type-editor-${kind}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent">{kind === "customer-types" ? "Customer Types" : "Vendor Types"} · {rows.length}</div>
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase text-muted-foreground border-b">
          <tr><th className="text-left py-2">Code</th><th className="text-left">Name</th><th className="text-left">{accountLabel}</th><th className="text-left">Description</th><th></th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} className="border-b" data-testid={`row-${r.code}`}>
              <td className="py-2 pr-2"><Input value={r.code || ""} onChange={(e) => update(i, { code: e.target.value })} className="rounded-sm font-mono h-8" /></td>
              <td className="pr-2"><Input value={r.name || ""} onChange={(e) => update(i, { name: e.target.value })} className="rounded-sm h-8" /></td>
              <td className="pr-2"><Input value={r[accountField] || ""} onChange={(e) => update(i, { [accountField]: e.target.value })} className="rounded-sm font-mono h-8" placeholder="e.g. 4001" /></td>
              <td className="pr-2"><Input value={r.description || ""} onChange={(e) => update(i, { description: e.target.value })} className="rounded-sm h-8" /></td>
              <td className="text-right flex gap-1 justify-end">
                {r._dirty && <Button size="sm" variant="outline" onClick={() => saveRow(r)} className="h-7 px-2"><Save className="w-3 h-3" /></Button>}
                <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="h-7 px-2 text-red-600"><Trash2 className="w-3 h-3" /></Button>
              </td>
            </tr>
          ))}
          <tr className="bg-muted/30">
            <td className="py-2 pr-2"><Input value={newRow.code} onChange={(e) => setNewRow({ ...newRow, code: e.target.value })} className="rounded-sm font-mono h-8" placeholder="code" data-testid="new-code" /></td>
            <td className="pr-2"><Input value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} className="rounded-sm h-8" placeholder="Name *" data-testid="new-name" /></td>
            <td className="pr-2"><Input value={newRow[accountField] || ""} onChange={(e) => setNewRow({ ...newRow, [accountField]: e.target.value })} className="rounded-sm font-mono h-8" /></td>
            <td className="pr-2"><Input value={newRow.description || ""} onChange={(e) => setNewRow({ ...newRow, description: e.target.value })} className="rounded-sm h-8" /></td>
            <td className="text-right">
              <Button size="sm" onClick={addNew} disabled={busy} className="h-7 px-2 bg-accent hover:bg-accent/90 text-white" data-testid="add-new"><Plus className="w-3 h-3" /></Button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PRReasonsEditor() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(null);
  const [bulkChain, setBulkChain] = useState([{ level: 1, approver_role: "manager", threshold_inr: 100000, sla_hours: 24 }]);
  const [showBulk, setShowBulk] = useState(false);

  const load = async () => setRows((await api.get("/master/pr-reasons")).data);
  useEffect(() => { load(); }, []);

  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const saveChain = async (reasonId, chain) => {
    await api.post(`/master/pr-reasons/${reasonId}/workflow`, { chain });
    toast.success("Workflow saved");
    await load();
  };

  const applyBulk = async () => {
    if (selected.size === 0) return toast.error("Select at least one reason");
    if (!window.confirm(`Apply this ${bulkChain.length}-level workflow to ${selected.size} PR reasons?`)) return;
    await api.post("/master/pr-reasons/bulk-workflow", { reason_ids: Array.from(selected), chain: bulkChain });
    toast.success(`✓ Applied to ${selected.size} reasons`);
    setShowBulk(false);
    setSelected(new Set());
    await load();
  };

  return (
    <div className="space-y-4" data-testid="pr-reasons-editor">
      <div className="card-sharp p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">PR Reasons · {rows.length}</div>
            <div className="text-xs text-muted-foreground mt-1">Each PR reason has its own approval chain. Tick multiple + hit "Bulk configure workflow" to apply to many at once.</div>
          </div>
          <Button onClick={() => setShowBulk(!showBulk)} disabled={selected.size === 0} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="bulk-configure">
            Bulk configure workflow ({selected.size})
          </Button>
        </div>

        {showBulk && (
          <div className="bg-accent/5 border border-accent/40 rounded-sm p-3 space-y-2" data-testid="bulk-panel">
            <div className="text-xs font-medium">Approval chain for {selected.size} selected reasons</div>
            <WorkflowChainEditor chain={bulkChain} onChange={setBulkChain} />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowBulk(false)} className="rounded-sm">Cancel</Button>
              <Button size="sm" onClick={applyBulk} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="apply-bulk">Apply</Button>
            </div>
          </div>
        )}

        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase text-muted-foreground border-b">
            <tr><th className="w-8"></th><th className="text-left py-2">Code</th><th className="text-left">Name</th><th className="text-left">Vendor Type</th><th className="text-left">Chain</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="border-b" data-testid={`pr-row-${r.code}`}>
                  <td><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} data-testid={`sel-${r.code}`} /></td>
                  <td className="py-2 font-mono text-xs">{r.code}</td>
                  <td>{r.name}</td>
                  <td className="text-muted-foreground">{r.vendor_type_code || "—"}</td>
                  <td>
                    <div className="flex gap-1 flex-wrap">
                      {(r.approval_chain || []).map((l, i) => (
                        <span key={i} className="text-[10px] bg-accent/10 border border-accent/30 px-1.5 py-0.5 rounded-sm">
                          L{l.level}·{l.approver_role}{l.threshold_inr ? ` >₹${l.threshold_inr}` : ""}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="h-7 text-accent" data-testid={`edit-${r.code}`}>
                      {expanded === r.id ? "Close" : "Edit chain"}
                    </Button>
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr>
                    <td colSpan={6} className="bg-muted/30 p-3">
                      <WorkflowChainEditor
                        chain={r.approval_chain || []}
                        onChange={(chain) => setRows(rs => rs.map(x => x.id === r.id ? { ...x, approval_chain: chain } : x))}
                      />
                      <div className="flex justify-end mt-2">
                        <Button size="sm" onClick={() => saveChain(r.id, r.approval_chain || [])} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid={`save-chain-${r.code}`}>Save chain</Button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WorkflowChainEditor({ chain, onChange }) {
  const upd = (i, patch) => onChange(chain.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const add = () => onChange([...chain, { level: chain.length + 1, approver_role: "manager", threshold_inr: null, sla_hours: 24 }]);
  const del = (i) => onChange(chain.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, level: idx + 1 })));
  return (
    <div className="space-y-2">
      {chain.map((l, i) => (
        <div key={i} className="flex items-center gap-2 text-xs" data-testid={`chain-level-${l.level}`}>
          <div className="font-mono w-8 text-accent">L{l.level}</div>
          <Input value={l.approver_role} onChange={(e) => upd(i, { approver_role: e.target.value })} className="rounded-sm h-8 max-w-[160px]" placeholder="role e.g. manager" />
          <span className="text-muted-foreground text-[10px]">when amount &gt;</span>
          <Input type="number" value={l.threshold_inr || ""} onChange={(e) => upd(i, { threshold_inr: e.target.value ? Number(e.target.value) : null })} className="rounded-sm h-8 max-w-[120px] font-mono" placeholder="₹ threshold" />
          <span className="text-muted-foreground text-[10px]">SLA</span>
          <Input type="number" value={l.sla_hours || ""} onChange={(e) => upd(i, { sla_hours: Number(e.target.value) })} className="rounded-sm h-8 max-w-[80px] font-mono" placeholder="hrs" />
          <Button size="sm" variant="ghost" onClick={() => del(i)} className="h-7 text-red-600"><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} className="rounded-sm gap-1"><Plus className="w-3 h-3" /> Add level</Button>
    </div>
  );
}
